import {
  GET_USER_SETTINGS,
  SAVE_USER_SETTINGS,
  SET_DEFAULT_SETTINGS,
  GET_USER_SETTINGS_SUCCESS,
  GET_USER_SETTINGS_FAILURE,
} from "../actions/actionTypes";

const initialState = {
  userSettings: null,
  error: null,
  loading: true,
  isLoaded: false,
};

const defaultSettings = {
  distanceRange: [0, 50],
  ageRange: [18, 99],
  genderPreferences: ["men", "women"],
  notificationEnabled: true,
  locationSharingEnabled: null, // null = user hasn't decided yet, true = enabled, false = denied
};

const normalizeSettings = (payload = {}) => {
  const getFallback = (key1, key2, defaultValue) => {
    return payload[key1] ?? payload[key2] ?? defaultValue;
  };

  return {
    ...defaultSettings,
    ...payload,
    distanceRange: getFallback(
      "distance_range",
      "distanceRange",
      defaultSettings.distanceRange,
    ),
    ageRange: getFallback("age_range", "ageRange", defaultSettings.ageRange),
    genderPreferences: getFallback(
      "gender_preferences",
      "genderPreferences",
      defaultSettings.genderPreferences,
    ).map((g) => g.toLowerCase()),
    notificationEnabled: getFallback(
      "notification_enabled",
      "notificationEnabled",
      defaultSettings.notificationEnabled,
    ),
    // Preserve null/undefined for locationSharingEnabled to distinguish "not decided" from "disabled"
    locationSharingEnabled:
      payload.location_sharing_enabled ??
      payload.locationSharingEnabled ??
      defaultSettings.locationSharingEnabled,
  };
};

const userSettingsReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_USER_SETTINGS:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case GET_USER_SETTINGS_SUCCESS:
      return {
        ...state,
        userSettings: normalizeSettings(action.payload),
        loading: false,
        error: null,
      };

    case GET_USER_SETTINGS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        userSettings: defaultSettings,
      };

    case SAVE_USER_SETTINGS: {
      let rawSettings = action.payload.userSettings ?? action.payload;

      if (rawSettings.settings) {
        rawSettings = { ...rawSettings.settings, ...rawSettings };
        delete rawSettings.settings;
      }

      // If existing state has locationSharingEnabled explicitly set to false
      // (user clicked Deny), preserve that unless new settings also have it
      const existingLocationPref = state.userSettings?.locationSharingEnabled;
      const newLocationPref =
        rawSettings.locationSharingEnabled ??
        rawSettings.location_sharing_enabled;

      // Only use the existing false preference if new settings don't explicitly set it
      if (existingLocationPref === false && newLocationPref === undefined) {
        rawSettings.locationSharingEnabled = false;
      }

      return {
        ...state,
        userSettings: normalizeSettings(rawSettings),
        loading: false,
        error: null,
      };
    }

    case SET_DEFAULT_SETTINGS:
      // Merge defaults with existing settings to preserve user choices (like locationSharingEnabled: false)
      return {
        ...state,
        userSettings: {
          ...defaultSettings,
          ...(state.userSettings || {}),
        },
        loading: false,
        error: null,
        isLoaded: true,
      };

    default:
      return state;
  }
};

export default userSettingsReducer;
